import { Trophy, Medal, Award, Star, Zap } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

export const ChampionshipStandingCard = ({
  position,
  teamName,
  ownerName,
  partnerName,
  matchesPlayed,
  wins,
  draws,
  losses,
  championshipPoints,
  totalFantasyPoints,
  isCurrentUser
}) => {
  const getMedalIcon = () => {
    if (position === 1) return <Trophy className="w-6 h-6 text-yellow-600 animate-bounce" />;
    if (position === 2) return <Medal className="w-6 h-6 text-gray-500 animate-pulse" />;
    if (position === 3) return <Medal className="w-6 h-6 text-amber-700 animate-pulse" />;
    return null;
  };

  const getPositionStyle = () => {
    if (position === 1) return 'bg-yellow-50/50 dark:bg-yellow-950/30 border-yellow-400 shadow-sm';
    if (position === 2) return 'bg-gray-50/50 dark:bg-gray-900/30 border-gray-400 shadow-sm';
    if (position === 3) return 'bg-amber-50/50 dark:bg-amber-950/30 border-amber-500 shadow-sm';
    return '';
  };

  return (
    <div className={`
      flex items-center p-4 rounded-lg border-2 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl
      ${position <= 3 ? getPositionStyle() : ''}
      ${isCurrentUser 
        ? 'border-primary bg-primary/5 shadow-md ring-2 ring-primary/20 hover:ring-4 hover:ring-primary/30' 
        : position > 3 ? 'border-border bg-card hover:shadow-lg' : ''
      }
    `}>
      {/* Position */}
      <div className="flex items-center gap-3 min-w-[60px]">
        {position <= 3 ? (
          <div className="flex items-center justify-center w-12 h-12">
            {getMedalIcon()}
          </div>
        ) : (
          <div className="flex items-center justify-center w-12 h-12">
            <span className="text-2xl font-bold text-muted-foreground">{position}</span>
          </div>
        )}
      </div>

      {/* Team Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Avatar className={`h-10 w-10 ${isCurrentUser ? 'bg-primary ring-2 ring-primary/50' : 'bg-primary'}`}>
            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
              {teamName.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className={`font-bold truncate text-base ${isCurrentUser ? 'text-primary' : 'text-gray-900 dark:text-gray-100'}`}>
              {teamName}
            </p>
            <p className="text-xs text-gray-700 dark:text-gray-300 truncate">
              {ownerName}
              {partnerName && ` & ${partnerName}`}
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="flex items-center gap-2 text-xs mt-2 flex-wrap">
          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-950/30 rounded">
            <span className="font-semibold text-gray-700 dark:text-gray-300">G:</span>
            <span className="font-bold text-gray-900 dark:text-gray-100">{matchesPlayed || 0}</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-950/30 rounded">
            <span className="text-green-700 dark:text-green-400 font-semibold">V:</span>
            <span className="text-green-700 dark:text-green-400 font-bold">{wins || 0}</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-yellow-50 dark:bg-yellow-950/30 rounded">
            <span className="text-yellow-700 dark:text-yellow-400 font-semibold">P:</span>
            <span className="text-yellow-700 dark:text-yellow-400 font-bold">{draws || 0}</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-red-50 dark:bg-red-950/30 rounded">
            <span className="text-red-700 dark:text-red-400 font-semibold">S:</span>
            <span className="text-red-700 dark:text-red-400 font-bold">{losses || 0}</span>
          </div>
        </div>
      </div>

      {/* Points */}
      <div className="flex flex-col items-end gap-1 ml-3">
        {/* Championship Points - Large and prominent */}
        <div className="text-right">
          <p className={`text-2xl sm:text-3xl font-bold text-primary leading-none transition-all duration-300 hover:scale-110 ${position === 1 ? 'animate-pulse' : ''}`}>
            {championshipPoints !== null && championshipPoints !== undefined ? Math.round(championshipPoints) : 0}
          </p>
          <p className="text-xs text-gray-700 dark:text-gray-300 font-medium mt-0.5">Pt</p>
        </div>
        
        {/* Fantasy Points - Compact */}
        <div className="text-right mt-1 px-2 py-0.5 bg-blue-50 dark:bg-blue-950/30 rounded min-w-[60px]">
          <p className="text-xs font-bold text-blue-600 dark:text-blue-400 leading-tight">
            {totalFantasyPoints !== null && totalFantasyPoints !== undefined ? Math.round(totalFantasyPoints) : 0}
          </p>
          <p className="text-[10px] text-gray-700 dark:text-gray-300 leading-tight">Pt Totali</p>
        </div>
      </div>
    </div>
  );
};
